import Phaser from 'phaser';
import { TEX, CLASS_COLORS } from '@/data/assetManifest';
import { useSettingsStore, getSettingsSnapshot } from '@/state/settingsStore';
import { usePlayerActionStore } from '@/state/playerActionStore';
import { isPhaserPointerOverBlockingUI } from '@/ui/pointerBlock';

export interface PlayerOpts {
  scene: Phaser.Scene;
  x: number;
  y: number;
  uid: string;
  name: string;
  klass: 'warrior' | 'wizard' | 'archer';
  isLocal?: boolean;
  gender?: number; // 0=male, 1=female
}

type Dir = 'down' | 'left' | 'right' | 'up';
type ActionState = 'idle' | 'walk' | 'attack' | 'hurt' | 'death';

// v3 hero sprites are rendered at native ~96-128px. Display scale tunes to
// match the tile world (~32px) without dwarfing monsters.
const PLAYER_SCALE = 0.55;

export class Player extends Phaser.GameObjects.Container {
  name: string;
  uid: string;
  klass: PlayerOpts['klass'];
  gender: number;
  isLocal: boolean;
  bodySprite!: Phaser.GameObjects.Sprite;
  shadow!: Phaser.GameObjects.Graphics;
  nameTag!: Phaser.GameObjects.Text;
  private nameplateUnsub?: () => void;
  private animPrefix: string;
  hp = 100;
  maxHp = 100;
  targetX: number;
  targetY: number;
  private lastX: number;
  private lastY: number;
  private dir: Dir = 'down';
  private moving = false;
  private actionState: ActionState = 'idle';
  private actionUntil = 0;

  constructor(opts: PlayerOpts) {
    super(opts.scene, opts.x, opts.y);
    this.uid = opts.uid;
    this.name = opts.name;
    this.klass = opts.klass;
    // Per ASSET_CHARACTER_REQUIREMENTS.md §11.2.1: Archer = female sprite,
    // Warrior/Wizard = male sprite. Class drives the visual until per-class
    // gender variants exist in the asset pipeline.
    this.gender = opts.klass === 'archer' ? 1 : 0;
    this.isLocal = !!opts.isLocal;
    this.targetX = opts.x;
    this.targetY = opts.y;
    this.lastX = opts.x;
    this.lastY = opts.y;
    this.animPrefix = this.gender === 1 ? 'body_female_v3' : 'body_male_v3';

    // Shadow ellipse under feet
    this.shadow = opts.scene.add.graphics();
    this.shadow.fillStyle(0x000000, 0.35);
    this.shadow.fillEllipse(0, 4, 36, 10);

    // Body sprite (v3 multi-anim hero base — male or female)
    const idleKey = this.gender === 1 ? TEX.bodyFemaleV3Idle : TEX.bodyMaleV3Idle;
    this.bodySprite = opts.scene.add.sprite(0, 0, idleKey)
      .setOrigin(0.5, 0.92)
      .setScale(PLAYER_SCALE);
    this.playAnim('idle', 'down');

    this.nameTag = opts.scene.add.text(0, -84, opts.name, {
      fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '12px', color: opts.isLocal ? '#ffd166' : '#ffffff',
      stroke: '#000', strokeThickness: 3,
    }).setOrigin(0.5);
    this.nameTag.setVisible(getSettingsSnapshot().showNameplates);
    this.nameplateUnsub = useSettingsStore.subscribe((s, prev) => {
      if (s.showNameplates !== prev.showNameplates) {
        this.nameTag?.setVisible(s.showNameplates);
      }
    });
    this.add([this.shadow, this.bodySprite, this.nameTag]);
    opts.scene.add.existing(this);
    this.setDepth(10);

    if (!this.isLocal) {
      this.setInteractive(
        new Phaser.Geom.Rectangle(-25, -68, 50, 72),
        Phaser.Geom.Rectangle.Contains,
      );
      this.on('pointerup', (pointer: Phaser.Input.Pointer) => {
        if (isPhaserPointerOverBlockingUI(pointer)) return;
        const ev = pointer.event as MouseEvent | undefined;
        const sx = ev && typeof ev.clientX === 'number' ? ev.clientX : pointer.x;
        const sy = ev && typeof ev.clientY === 'number' ? ev.clientY : pointer.y;
        usePlayerActionStore.getState().open({
          uid: this.uid,
          name: this.name,
          klass: this.klass,
          screenX: sx,
          screenY: sy,
        });
      });
    }
  }

  private playAnim(state: ActionState, dir: Dir) {
    if (!this.scene) return;
    // Source v3 sheets: the "LEFT" rows actually depict the character facing
    // RIGHT (model produced inverted side profiles). Use those frames for
    // RIGHT direction as-is, and flipX them when moving LEFT.
    const useFlip = dir === 'left';
    const animDir: Dir = (dir === 'left' || dir === 'right') ? 'left' : dir;
    this.bodySprite.setFlipX(useFlip);
    const key = state === 'death'
      ? `${this.animPrefix}_death`
      : `${this.animPrefix}_${state}_${animDir}`;
    if (!this.scene.anims.exists(key)) return;
    if (this.bodySprite.anims.currentAnim?.key !== key) {
      this.bodySprite.play(key);
    }
  }

  setRemoteTarget(x: number, y: number) {
    this.targetX = x; this.targetY = y;
  }

  setHp(hp: number, maxHp: number) {
    this.hp = hp; this.maxHp = Math.max(1, maxHp);
  }

  // Trigger one-shot states (attack/hurt/death). Reverts to walk/idle when
  // actionUntil expires (death is permanent — caller must destroy the player).
  triggerAction(state: 'attack' | 'cast' | 'hurt' | 'death', durationMs: number) {
    // 'cast' isn't in v3 sheets — fall back to attack visuals.
    const mapped: ActionState = state === 'cast' ? 'attack' : state;
    this.actionState = mapped;
    this.actionUntil = (this.scene?.time?.now ?? 0) + durationMs;
    this.playAnim(mapped, this.dir);
  }

  preUpdate(_time: number, delta: number) {
    if (!this.isLocal) {
      const k = Math.min(1, delta / 100);
      const nx = this.x + (this.targetX - this.x) * k;
      const ny = this.y + (this.targetY - this.y) * k;
      const dx = nx - this.x;
      const dy = ny - this.y;
      if (Math.abs(dx) + Math.abs(dy) > 0.05) {
        this.updateFacing(dx, dy);
      } else {
        this.moving = false;
      }
      this.x = nx; this.y = ny;
      this.applyAnim();
      this.lastX = this.x; this.lastY = this.y;
      return;
    }
    const dx = this.x - this.lastX;
    const dy = this.y - this.lastY;
    if (Math.abs(dx) + Math.abs(dy) > 0.05) {
      this.updateFacing(dx, dy);
    } else {
      this.moving = false;
    }
    this.applyAnim();
    this.lastX = this.x; this.lastY = this.y;
  }

  private updateFacing(dx: number, dy: number) {
    this.moving = true;
    if (Math.abs(dx) >= Math.abs(dy)) {
      this.dir = dx > 0 ? 'right' : 'left';
    } else {
      this.dir = dy > 0 ? 'down' : 'up';
    }
  }

  private applyAnim() {
    if (!this.scene) return;
    if (this.actionState !== 'idle' && this.actionState !== 'walk') {
      if (this.actionState === 'death') {
        this.playAnim('death', this.dir);
        return;
      }
      if ((this.scene.time?.now ?? 0) < this.actionUntil) {
        this.playAnim(this.actionState, this.dir);
        return;
      }
      this.actionState = 'idle';
    }
    const next: ActionState = this.moving ? 'walk' : 'idle';
    this.playAnim(next, this.dir);
  }

  showBubble(text: string, duration = 4000) {
    const maxW = 120;
    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: '"Philosopher", "Cinzel", serif', fontSize: '11px', color: '#fff',
      wordWrap: { width: maxW }, align: 'center',
    };
    const bubble = this.scene.add.text(0, -96, text, style).setOrigin(0.5, 1).setDepth(100);
    this.add(bubble);
    this.scene.time.delayedCall(duration, () => { bubble.destroy(); });
  }

  destroy(fromScene?: boolean) {
    this.nameplateUnsub?.();
    this.bodySprite.destroy();
    this.shadow.destroy();
    this.nameTag.destroy();
    super.destroy(fromScene);
  }
}

// Suppress unused-CLASS_COLORS warning while keeping import for compatibility
void CLASS_COLORS;
